import { memo, useState, useEffect, useCallback, useMemo } from 'react'
import { useActiveChannelId, useHasPermission } from '../../store/index.js'
import type { AdCampaign, Advertiser } from '../../store/slices/adsSlice.js'
import type { CampaignPacing, ChannelInventory } from '../../lib/adsApi.js'
import { campaignsApi, advertisersApi } from '../../lib/adsApi.js'
import { AdsAttentionBar }   from './AdsAttentionBar.js'
import { ATTENTION_VERDICTS, type AttentionKey } from '../../lib/adsPacing.js'
import { CampaignList, CampaignDetail, type CampaignView } from './CampaignList.js'
import { NewCampaignModal }  from './NewCampaignModal.js'
import { AdvertisersTab }    from './AdvertisersTab.js'
import { AdTransitionSettingsTab } from './AdTransitionSettingsTab.js'
import { PageHeader, PageTabs } from '../common/PageChrome.js'
import { ResizeHandle } from '../common/ResizeHandle.js'
import { Spinner } from '../common/Spinner.js'
import './AdsView.css'

// ─── Poignée de redimensionnement (identique à ProgramsView) ────────────────

const MIN_LEFT = 220
const MAX_LEFT = 560

// ─── Onglets ──────────────────────────────────────────────────────────────────

type AdsTabId = 'campaigns' | 'advertisers' | 'transitions'

const TABS: { id: AdsTabId; label: string }[] = [
  { id: 'campaigns',   label: 'Campagnes'  },
  { id: 'advertisers', label: 'Annonceurs' },
  { id: 'transitions', label: 'Entre les pubs' },
]

// ─── Composant racine ─────────────────────────────────────────────────────────

export const AdsView = memo(function AdsView() {
  const channelId  = useActiveChannelId()
  const canManage = useHasPermission('ADS_MANAGE')
  const canManageTransitions = useHasPermission('TECHNICAL_SETTINGS_MANAGE')
  const [campaigns,    setCampaigns]    = useState<AdCampaign[]>([])
  const [advertisers,  setAdvertisers]  = useState<Advertiser[]>([])
  const [loading,      setLoading]      = useState(true)
  const [selectedId,   setSelectedId]   = useState<string | null>(null)
  const [showModal,    setShowModal]    = useState(false)
  const [activeTab,    setActiveTab]    = useState<AdsTabId>('campaigns')
  const [leftWidth,    setLeftWidth]    = useState(280)
  const [inventory,    setInventory]    = useState<ChannelInventory | null>(null)
  const [attention,    setAttention]    = useState<AttentionKey | null>(null)
  // Le filtre est porté ici : basculer sur « Archivées » change la requête, pas
  // seulement l'affichage — le serveur ne renvoie les campagnes closes que sur
  // demande explicite.
  const [view,         setView]         = useState<CampaignView>('ALL')

  const onLeftResize = useCallback((delta: number) => {
    setLeftWidth(w => Math.min(MAX_LEFT, Math.max(MIN_LEFT, w + delta)))
  }, [])

  const fetchAdvertisers = useCallback(() => {
    advertisersApi.list().then(setAdvertisers).catch(console.error)
  }, [])

  useEffect(() => {
    if (!channelId) return
    setLoading(true)
    Promise.all([
      campaignsApi.list({ channelId, archived: view === 'ARCHIVED' ? 'archived' : 'active' }),
      advertisersApi.list(),
    ]).then(([camps, advs]) => {
      setCampaigns(camps)
      setAdvertisers(advs)
      // La sélection ne survit pas à un changement de vue : la campagne choisie
      // dans le catalogue courant n'existe pas dans la liste des archives, et
      // le détail afficherait alors une fiche absente de la liste à sa gauche.
      setSelectedId(previous => (camps.some(c => c.id === previous) ? previous : camps[0]?.id ?? null))
    }).catch(console.error).finally(() => setLoading(false))
  }, [channelId, view])

  // L'inventaire relit la grille entière : il est chargé à part pour que le
  // catalogue s'affiche sans l'attendre, et rechargé après toute opération qui
  // pose ou retire des placements.
  const fetchInventory = useCallback(() => {
    if (!channelId) return
    campaignsApi.pacing(channelId).then(setInventory).catch(console.error)
  }, [channelId])

  useEffect(fetchInventory, [fetchInventory])

  const pacingById = useMemo(
    () => new Map<string, CampaignPacing>((inventory?.campaigns ?? []).map(pacing => [pacing.campaignId, pacing])),
    [inventory],
  )
  const verdictFilter = attention ? ATTENTION_VERDICTS[attention] : null

  const selectedCampaign = campaigns.find(c => c.id === selectedId) ?? null

  const handleSelect  = useCallback((c: AdCampaign) => setSelectedId(c.id), [])

  const handleUpdated = useCallback((updated: AdCampaign) => {
    // Archiver ou restaurer fait changer la campagne de vue : la garder dans la
    // liste courante afficherait une archive au milieu du catalogue actif.
    const belongsHere = (updated.archivedAt !== null) === (view === 'ARCHIVED')
    if (!belongsHere) {
      setCampaigns(prev => {
        const remaining = prev.filter(c => c.id !== updated.id)
        setSelectedId(current => (current === updated.id ? remaining[0]?.id ?? null : current))
        return remaining
      })
      fetchAdvertisers()
      fetchInventory()
      return
    }
    setCampaigns(prev => prev.map(c => c.id === updated.id ? updated : c))
    // Resynchronise les annonceurs au cas où l'opération en aurait créé un.
    fetchAdvertisers()
    // Un placement automatique, un changement de dates ou de fréquence change
    // la portée réelle : l'inventaire ne peut pas rester sur son ancienne photo.
    fetchInventory()
  }, [fetchAdvertisers, fetchInventory, view])

  const handleDeleted = useCallback(() => {
    setCampaigns(prev => {
      const filtered = prev.filter(c => c.id !== selectedId)
      setSelectedId(filtered[0]?.id ?? null)
      return filtered
    })
  }, [selectedId])

  const handleDeleteMany = useCallback(async (ids: string[]) => {
    // Une seule requête : le serveur vérifie lui-même que chaque campagne est
    // bien archivée, et rend le détail de ce qu'il a refusé.
    const result = await campaignsApi.bulkDelete(ids)
    setCampaigns(previous => {
      const remaining = previous.filter(campaign => !result.deleted.includes(campaign.id))
      setSelectedId(current => current && result.deleted.includes(current) ? remaining[0]?.id ?? null : current)
      return remaining
    })
    if (result.refused.length > 0) {
      alert(`${result.refused.length} campagne${result.refused.length > 1 ? 's' : ''} non archivée${result.refused.length > 1 ? 's' : ''} : archivez-la d'abord.`)
    }
    return result.deleted
  }, [])

  const handleCreated = useCallback((c: AdCampaign) => {
    // Une création arrive toujours au catalogue courant : si l'opérateur
    // regardait les archives, on l'y ramène plutôt que de créer dans le vide.
    setView(current => (current === 'ARCHIVED' ? 'ALL' : current))
    setCampaigns(prev => [c, ...prev])
    setSelectedId(c.id)
    setShowModal(false)
    // La création peut avoir créé un nouvel annonceur (option « + Nouvel
    // annonceur » dans la modal) — on rafraîchit la liste pour qu'il apparaisse
    // dans l'onglet Annonceurs ainsi que dans le sélecteur des prochaines créations.
    fetchAdvertisers()
  }, [fetchAdvertisers])

  const handleOpenModal = useCallback(() => setShowModal(true), [])
  const handleCloseModal = useCallback(() => setShowModal(false), [])
  const handleTabChange = useCallback((tabId: string) => setActiveTab(tabId as AdsTabId), [])
  const handleBackToCampaigns = useCallback(() => setSelectedId(null), [])

  return (
    <div className="tp-page-shell ads-view">

      {/* ── Topbar ─────────────────────────────────────────────────────────── */}
      <PageHeader
        title="Régie publicitaire"
        actions={activeTab === 'campaigns' ? (
          <button
            type="button"
            onClick={handleOpenModal}
            disabled={!channelId || !canManage}
            title={!canManage ? 'Permission de gestion publicitaire requise' : undefined}
            className="tp-button tp-button--primary"
            aria-label="Nouvelle campagne"
          >
            + Nouvelle campagne
          </button>
        ) : undefined}
      />

      {/* ── Onglets ─────────────────────────────────────────────────────────── */}
      <PageTabs
        items={canManageTransitions ? TABS : TABS.filter(tab => tab.id !== 'transitions')}
        activeId={activeTab}
        onChange={handleTabChange}
        ariaLabel="Sections de la régie publicitaire"
        controlsId="ads-tab-panel"
        idPrefix="ads-tab"
      />

      {/* ── Contenu ─────────────────────────────────────────────────────────── */}
      {activeTab === 'campaigns' && (
        <fieldset
          id="ads-tab-panel"
          className={`ads-workspace catalog-permission-fieldset${selectedCampaign ? ' has-selection' : ''}`}
          role="tabpanel"
          aria-labelledby="ads-tab-campaigns"
          disabled={!canManage}
          title={!canManage ? 'Consultation seule — permission de gestion publicitaire requise' : undefined}
        >

          {/* Colonne gauche redimensionnable : dashboard + liste des campagnes */}
          <aside
            className="ads-list-pane"
            aria-label="Catalogue des campagnes"
            style={{ '--ads-list-width': `${leftWidth}px` } as React.CSSProperties}
          >
            {!loading && (
              <AdsAttentionBar inventory={inventory} activeFilter={attention} onFilter={setAttention} />
            )}
            {/* Wrapper avec flex:1 + minHeight:0 pour que le scroll de CampaignList fonctionne */}
            <div className="ads-list-pane__list">
              <CampaignList
                campaigns={campaigns}
                selectedId={selectedId}
                onSelect={handleSelect}
                onNew={handleOpenModal}
                onDeleteMany={handleDeleteMany}
                pacingById={pacingById}
                verdictFilter={verdictFilter}
                view={view}
                onViewChange={setView}
              />
            </div>
          </aside>

          <ResizeHandle label="Redimensionner le catalogue des campagnes" onDelta={onLeftResize} />

          {/* Colonne droite : détail */}
          <main className="ads-detail-pane">
            {selectedCampaign && (
              <div className="ads-detail-mobile-bar">
                <button type="button" className="tp-button" onClick={handleBackToCampaigns}>← Campagnes</button>
              </div>
            )}
            {loading ? (
              <div className="tp-empty-state ads-detail-state" aria-live="polite"><Spinner /> Chargement…</div>
            ) : selectedCampaign ? (
              <CampaignDetail
                campaign={selectedCampaign}
                pacing={pacingById.get(selectedCampaign.id) ?? null}
                onUpdated={handleUpdated}
                onDeleted={handleDeleted}
              />
            ) : (
              <div className="tp-empty-state ads-detail-state">
                {campaigns.length === 0 ? 'Aucune campagne — créez-en une !' : 'Sélectionnez une campagne'}
              </div>
            )}
          </main>
        </fieldset>
      )}

      {activeTab === 'advertisers' && (
        <fieldset id="ads-tab-panel" role="tabpanel" aria-labelledby="ads-tab-advertisers" className="ads-tab-panel catalog-permission-fieldset" disabled={!canManage} title={!canManage ? 'Consultation seule — permission de gestion publicitaire requise' : undefined}>
          <AdvertisersTab advertisers={advertisers} onUpdated={fetchAdvertisers} />
        </fieldset>
      )}

      {activeTab === 'transitions' && canManageTransitions && (
        <AdTransitionSettingsTab channelId={channelId} canManage={canManage} />
      )}

      {/* ── Modal nouvelle campagne ─────────────────────────────────────────── */}
      {showModal && channelId && canManage && (
        <NewCampaignModal
          channelId={channelId}
          advertisers={advertisers}
          onCreated={handleCreated}
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
})
